import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Cell, Row } from '@tanstack/react-table'
import { Fragment, useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { KeyValue } from '@ui/interfaces'
import { useTabSelectorStore, useTelemetryStore } from '@ui/stores'
import clsx from 'clsx'
import dayjs from 'dayjs'
import * as Diff from 'diff'

interface DBLBodyProps {
  rows: Row<KeyValue>[]
  dataSource: 'logs' | 'spans' | 'metrics' | string
  tableType: 'input' | 'panelInput' | 'output' | 'data'
  setDiffIndex: (index: number) => void
  setEventViewIndex: (index: number) => void
  handleRowClick?: ({ row }: { row: Row<KeyValue> }) => void
  setModalRow?: (row: Row<KeyValue> | null) => void
  activeRow?: number | null
  condensedStyle?: boolean
}

export const DBLBody = ({
  rows,
  dataSource,
  tableType,
  setDiffIndex,
  setEventViewIndex,
  handleRowClick,
  setModalRow,
  activeRow,
  condensedStyle = false,
}: DBLBodyProps) => {
  const { setCurrentTab } = useTabSelectorStore(
    useShallow((state) => ({
      setCurrentTab: state.setCurrentTab,
    }))
  )
  const { setSelectedEvent } = useTelemetryStore(
    useShallow((state) => ({
      setSelectedEvent: state.setSelectedEvent,
    }))
  )
  const showDiff = useCallback(
    (index: number) => {
      setDiffIndex(index)
      setEventViewIndex(1)
    },
    [setDiffIndex, setEventViewIndex]
  )

  const selectEvent = (row: Row<KeyValue>) => {
    setSelectedEvent(row.original.input)
  }
  const formatCellContent = (columnId: string, val: unknown) => {
    if (columnId === 'timestamp' && (val instanceof Date || typeof val === 'number')) {
      return <div className="flex h-full flex-col justify-center px-1">{dayjs(val).format('YYYY-MM-DD HH:mm:ss')}</div>
    }

    if (typeof val === 'object') {
      return <div className="flex h-full flex-col justify-center px-1">{JSON.stringify(val)}</div>
    }

    return <div className="flex h-full flex-col justify-center px-1">{val as string}</div>
  }

  const renderCellContent = useCallback(
    (cell: Cell<KeyValue, unknown>) => {
      const cellValue = cell.getValue() as { val: string; diff: Diff.Change[] }
      const { diff, val } = cellValue

      switch (tableType) {
        case 'input':
        case 'data':
          return (
            <div className="flex h-full flex-col justify-center px-1">{formatCellContent(cell.column.id, val)}</div>
          )
        case 'panelInput':
          if (cell.column.id === 'expander') {
            const Icon = cell.row.getIsExpanded() ? ChevronDownIcon : ChevronRightIcon
            return (
              <div
                className={clsx('flex w-full items-center justify-center', {
                  'h-8': condensedStyle,
                  'h-full': !condensedStyle,
                })}
                {...{
                  onClick: cell.row.getToggleExpandedHandler(),
                }}>
                <Icon className={clsx('h-4 w-4 cursor-pointer text-primary-600', { 'h-3 w-3': condensedStyle })} />
              </div>
            )
          }

          return (
            <div
              onClick={() => cell.row.getToggleExpandedHandler()}
              className="flex h-full flex-col justify-center px-1">
              {formatCellContent(cell.column.id, val)}
            </div>
          )
        case 'output':
          if (cell.column.id === 'expander') {
            const Icon = cell.row.getIsExpanded() ? ChevronDownIcon : ChevronRightIcon
            return (
              <div
                className={clsx('flex w-full items-center justify-center', {
                  'h-8': condensedStyle,
                  'h-full': !condensedStyle,
                })}
                {...{
                  onClick: cell.row.getToggleExpandedHandler(),
                }}>
                <Icon className={clsx('h-4 w-4 cursor-pointer text-primary-600', { 'h-3 w-3': condensedStyle })} />
              </div>
            )
          }

          // Cell was deleted show it to the user in the first column
          // timestamp should always be the first column
          if (cell.row.original.output == null) {
            if (cell.column.id === 'timestamp') {
              return (
                <div
                  className={'flex h-full flex-col justify-center bg-gray-100 px-1'}
                  onClick={() => showDiff(cell.row.index)}>
                  <p className="text-sm italic text-gray-500">Event deleted</p>
                </div>
              )
            } else {
              return <div className={'flex h-full flex-col bg-gray-100 px-2 py-3'} />
            }
          }

          return (
            <div
              className={clsx('flex h-full flex-col justify-center px-1', {
                'bg-green-200': diff.some((part: Diff.Change) => part.added),
                'bg-primary-200': diff.some((part: Diff.Change) => part.removed),
                '': !diff.some((part: Diff.Change) => part.added) && !diff.some((part: Diff.Change) => part.removed),
              })}>
              {diff.map((part: Diff.Change, index: number) => {
                return <span key={index}>{formatCellContent(cell.column.id, part.value)}</span>
              })}
            </div>
          )
      }
    },
    [tableType, condensedStyle, showDiff]
  )

  const renderDiff = useCallback(
    ({ row }: { row: Row<KeyValue> }) => {
      const diff = Diff.diffJson(row.original.input, row.original.output)

      const handlePartString = (part: Diff.Change) => {
        if (part.value === 'null') {
          return ''
        }
        if (part.added) {
          // make stringified json parts more readable
          if (part.value.startsWith('{')) {
            part.value = JSON.stringify(JSON.parse(part.value), null, 2)
          }

          part.value = part.value.trim().split('\n').join('\n+ ')
          return `+ ${part.value}`
        }
        if (part.removed) {
          part.value = part.value.trim().split('\n').join('\n- ')
          return `- ${part.value}`
        }

        return part.value
      }

      if (tableType === 'panelInput') {
        return (
          <div className={'flex w-full border border-dashed border-slate-800/20 p-2'}>
            <pre className="flex w-full flex-col overflow-x-auto whitespace-pre-line text-xs leading-3">
              {Diff.diffJson(row.original.input, row.original.input).map((part: Diff.Change, index: number) => {
                return (
                  <span className="font-md w-full text-xs" key={index}>
                    {handlePartString(part)}
                  </span>
                )
              })}
            </pre>
          </div>
        )
      }

      return (
        <div className={'flex w-full border border-dashed border-slate-800/20 p-2'}>
          <pre className="flex w-full flex-col whitespace-pre-wrap break-all text-xs leading-4">
            {diff.map((part: Diff.Change, index: number) => {
              return (
                <span
                  key={index}
                  className={clsx('font-md w-full text-xs', {
                    'bg-green-200': part.added,
                    'bg-primary-200': part.removed,
                    '': !part.added && !part.removed,
                  })}>
                  {handlePartString(part)}
                </span>
              )
            })}
          </pre>
        </div>
      )
    },
    [tableType]
  )

  const handleTableRowClick = (row: Row<KeyValue>) => {
    switch (tableType) {
      case 'input':
        selectEvent(row)
        if (setModalRow) setModalRow(row)
        setCurrentTab('Code')
        break
      case 'panelInput':
      case 'output':
        row.getToggleExpandedHandler()()
        break
      case 'data':
        if (handleRowClick) {
          handleRowClick({ row })
        }
        break
    }
  }
  const rowWidth = useMemo(() => {
    if (condensedStyle) {
      return (row: Row<KeyValue>) => {
        const visibleCells = row._getAllVisibleCells()
        const firstCellSize = visibleCells[0]?.column.getSize() ?? 0
        const secondCellSize = visibleCells[1]?.column.getSize() ?? 0
        const thirdCellSize = visibleCells[2]?.column.getSize() ?? 0
        const fourthCellSize = visibleCells[3]?.column.getSize() ?? 0
        return firstCellSize + secondCellSize + thirdCellSize + fourthCellSize
      }
    }
    return () => '100%'
  }, [condensedStyle])

  return (
    <div>
      {rows.map((row) => {
        return (
          <Fragment key={`${dataSource}-${row.id}`}>
            <div
              className={clsx('flex cursor-pointer flex-row border-b hover:bg-primary-100', {
                'bg-primary-100': row.index === activeRow,
              })}
              data-testid={`telemetry-row-${row.index}`}
              onClick={() => handleTableRowClick(row)}>
              {row._getAllVisibleCells().map((cell) => {
                return (
                  <div
                    key={`${dataSource}-${cell.id}`}
                    className={clsx('h-10 truncate font-mono text-sm text-gray-900 group-hover:visible', {
                      'h-8 first:border-r-0': condensedStyle,
                      //   'last:sticky last:right-0': whichTableToDisplay === 'input',
                    })}
                    style={{
                      width: cell.column.getSize(),
                    }}>
                    {renderCellContent(cell)}
                  </div>
                )
              })}
            </div>
            {row.getIsExpanded() && (
              <div
                style={{
                  width: rowWidth(row),
                }}
                className="whitespace-pre bg-gray-100 text-sm">
                {/* 2nd row is a custom 1 cell row */}
                {renderDiff({ row })}
              </div>
            )}
          </Fragment>
        )
      })}
    </div>
  )
}
